import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Alert,
  Input,
  Label,
  Badge,
} from "reactstrap";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import moment from "moment";

import {
  getAllDiscover,
  createDiscover,
  deleteDiscover,
  updateDiscoverName,
} from "api";
import { handleError } from "utils/errorHandler";
import DeleteModal from "components/Common/DeleteModal";
const DiscoverManagement = () => {
  const [discovers, setDiscovers] = useState([]);
  const [items, setItems] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [originalDiscovers, setOriginalDiscovers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

const loadDiscovers = async () => {
  try {
    setLoading(true);

    const response = await getAllDiscover(1);
    const data = response.data || [];
    const pagination = response.pagination || {};

    setDiscovers(data);
    setOriginalDiscovers(data);
    setCurrentPage(pagination.page || 1);
    setHasMore(pagination.hasNext || false);

  } catch (err) {
    setError("Failed to fetch discover items");
  } finally {
    setLoading(false);
  }
};
const loadMoreDiscover = async () => {
  if (isLoadingMore || !hasMore) return;

  try {
    setIsLoadingMore(true);

    const nextPage = currentPage + 1;
    const response = await getAllDiscover(nextPage);

    const newItems = response.data || [];
    const pagination = response.pagination || {};

    if (newItems.length > 0) {
      setDiscovers(prev => [...prev, ...newItems]);
      setOriginalDiscovers(prev => [...prev, ...newItems]);
      setCurrentPage(pagination.page || nextPage);
      setHasMore(pagination.hasNext || false);
    } else {
      setHasMore(false);
    }

  } catch (err) {
    console.error("Error loading more discover items:", err);
  } finally {
    setIsLoadingMore(false);
  }
};


  useEffect(() => {
    loadDiscovers();
  }, []);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const onDropAccepted = useCallback((acceptedFiles) => {
    const mapped = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      eventName: "",
    }));

    setItems((prev) => [...prev, ...mapped]);
  }, []);
  const onDropRejected = useCallback((fileRejections) => {
    fileRejections.forEach(({ file, errors }) => {
      errors.forEach((err) => {
        if (err.code === "file-too-large") {
          toast.error(`${file.name} exceeds 10MB limit`);
        }
        if (err.code === "file-invalid-type") {
          toast.error(`${file.name} has invalid file type`);
        }
      });
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    onDropRejected,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/webp": [],
    },
  });
  useEffect(() => {
    if (localStorage.getItem("authUser")) {
      try {
        const adminData = JSON.parse(localStorage.getItem("authUser"));
  
        if (adminData.user.userId) {
          const userId = Number(adminData.user.userId);
  
          if (Number.isInteger(userId)) {
            setCurrentUserId(userId);
          } else {
            console.error("Invalid userId in authUser:", userId);
          }
        }
      } catch (err) {
        console.error("Failed to parse authUser", err);
      }
    }
  }, []);
  
  const updateTitle = (index, value) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, eventName: value } : item
      )
    );
  };
  useEffect(() => {
    let timeout;
  
    const handleScroll = () => {
      clearTimeout(timeout);
  
      timeout = setTimeout(() => {
        const scrollTop = window.pageYOffset;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
  
        const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
  
        if (distanceFromBottom < 200 && hasMore && !isLoadingMore) {
          loadMoreDiscover();
        }
      }, 150);
    };
  
    window.addEventListener("scroll", handleScroll, { passive: true });
  
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeout);
    };
  }, [currentPage, hasMore, isLoadingMore]);
  

  const removeItem = (index) => {
    setItems((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };


  const handleSave = async () => {
    if (!items.length) {
      toast.error("Please select at least one image");
      return;
    }

    const missingTitle = items.some(
      (item) => !item.eventName.trim()
    );

    if (missingTitle) {toast.error("Each image must have a caption");
      
      return;
    }

    try {
      setLoading(true);
      setError(null);

      for (const item of items) {
        const formData = new FormData();
        formData.append("eventName", item.eventName);
        formData.append("createdBy",String(currentUserId )); 
        formData.append("file", item.file);

        await createDiscover(formData);
      }

      toast.success("Discover items created successfully");
      setItems([]);
      loadDiscovers();
    } catch (err) {
      handleError(err);
      setError("Failed to create discover items");
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateDiscover = async (id, eventName) => {
    console.log("handleUpdateDiscover called", id, eventName);
  
    try {
      setUpdatingId(id);
      setError(null);
  
      const res = await updateDiscoverName(id, eventName);
  
      console.log("API response:", res);
  
      toast.success("Caption updated successfully");
      await loadDiscovers();
    } catch (err) {
      console.error("Update error:", err);
      handleError(err);
      setError("Failed to update caption");
    } finally {
      setUpdatingId(null);
    }
  };
  
  const handleDelete = async () => {
    try {
      await deleteDiscover(selectedId);
      loadDiscovers();
    } catch (err) {
      handleError(err);
    } finally {
      setDeleteModal(false);
      setSelectedId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const m = moment(dateStr);
    return m.isValid() ? m.format("MMM D, YYYY h:mm A") : dateStr;
  };

  return (
    
          <div className="page-content">
            <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedId(null);
        }}
      />

        <Container fluid>
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="mb-3">
                  <i className="mdi mdi-information-outline me-2 text-primary" />
                  Discover Management
                </h4>

                {/* DROPZONE */}
                <div
                  {...getRootProps()}
                  className="border border-dashed rounded p-5 text-center mb-4"
                  style={{ cursor: "pointer" }}
                >
                  <input {...getInputProps()} />
                  Drag & drop images here, or click to select
                </div>

                {/* SELECTED ITEMS */}
                <Row>
                  {items.map((item, index) => (
                    <Col lg="3" md="4" key={index} className="mb-4">
                      <div className="card border-0 shadow-sm rounded-3 overflow-hidden h-100">
                        <div
                          className="position-relative bg-light"
                          style={{ height: 160, overflow: "hidden" }}
                        >
                          <img
                            src={item.preview}
                            alt=""
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute rounded-circle p-0 border-0 shadow-sm"
                            style={{
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => removeItem(index)}
                            title="Remove"
                          >
                            <i className="mdi mdi-delete font-size-14" />
                          </button>
                        </div>
                        <div className="card-body p-3">
                          <Label className="form-label small text-muted mb-1">Caption</Label>
                          <Input
                            placeholder="Enter a caption"
                            value={item.eventName}
                            onChange={(e) => updateTitle(index, e.target.value)}
                            className="form-control-sm"
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                {/* SAVE */}
                <div className="d-flex justify-content-start mt-3">
                  <Button
                    color="secondary"
                    onClick={handleSave}
                    disabled={loading}
                    style={{ minWidth: 120 }}
                  >
                    {loading ? <Spinner size="sm" /> : "Save"}
                  </Button>
                </div>

                {error && (
                  <Alert color="primary" className="mt-3">
                    {error}
                  </Alert>
                )}

                {/* EXISTING DISCOVERS */}
                <h5 className="mt-5 mb-3">
                  <i className="mdi mdi-image-multiple me-2 text-muted" />
                  Discover feed
                </h5>
                <Row>
                  {discovers.map((d) => (
                    <Col lg="2" md="3" key={d.id} className="mb-4">
                      <Card className="overflow-hidden border-0 shadow-sm h-100 rounded-3">
                        <div
                          className="position-relative bg-light"
                          style={{
                            height: 160,
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={d.eventImage}
                            alt={d.eventName || "Discover"}
                            className="w-100 h-100"
                            style={{
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute rounded-circle p-0 shadow-sm border-0"
                            style={{
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => {
                              setSelectedId(d.id);
                              setDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <i className="mdi mdi-delete font-size-14" />
                          </button>
                        </div>
                        <CardBody className="p-3">
                          <div className="mb-2">
                            <Label className="form-label small text-muted mb-1">Caption</Label>
                            <div className="d-flex gap-2 align-items-start">
                              <Input
                                type="text"
                                size="sm"
                                className="form-control form-control-sm"
                                value={d.eventName || ""}
                                onChange={(e) => {
                                  setDiscovers((prev) =>
                                    prev.map((x) =>
                                      x.id === d.id ? { ...x, eventName: e.target.value } : x
                                    )
                                  );
                                }}
                                placeholder="Enter caption"
                              />
                              {originalDiscovers.find((x) => x.id === d.id)?.eventName !== d.eventName && (
                                <Button
                                  type="button"
                                  size="sm"
                                  color="primary"
                                  className="btn-sm"
                                  disabled={updatingId === d.id}
                                  onClick={() => handleUpdateDiscover(d.id, d.eventName)}
                                  style={{ flexShrink: 0 }}
                                >
                                  {updatingId === d.id ? <Spinner size="sm" /> : "Save"}
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="d-flex flex-wrap gap-1 mb-2">
                            <Badge color="light" className="text-dark border px-2 py-1">
                              <i className="mdi mdi-eye-outline me-1 font-size-12" />
                              <span className="small">{typeof d.viewCount === "number" ? d.viewCount : d.viewCount ?? 0}</span>
                            </Badge>
                            <Badge color="light" className="text-dark border px-2 py-1">
                              <i className="mdi mdi-heart-outline me-1 font-size-12" />
                              <span className="small">{typeof d.likeCount === "number" ? d.likeCount : d.likeCount ?? 0}</span>
                            </Badge>
                          </div>

                          {d.creator && (
                            <div className="small text-muted mb-2">
                              <span className="d-block text-truncate">
                                <i className="mdi mdi-account-outline me-1" />
                                {d.creator.name || "—"}
                              </span>
                              {d.creator.email && (
                                <span className="d-block text-truncate" title={d.creator.email}>
                                  <i className="mdi mdi-email-outline me-1" />
                                  {d.creator.email}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="small text-muted border-top pt-2 mt-2">
                            <span className="d-block">
                              <i className="mdi mdi-calendar-plus me-1" />
                              Created: {formatDate(d.createdDate)}
                            </span>
                            {d.updatedDate && d.updatedDate !== d.createdDate && (
                              <span className="d-block">
                                <i className="mdi mdi-calendar-edit me-1" />
                                Updated: {formatDate(d.updatedDate)}
                              </span>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {isLoadingMore && (
                  <div className="text-center py-4">
                    <Spinner size="sm" />
                  </div>
                )}

                {!hasMore && (
                  <div className="text-center py-4 text-muted">
                    You’ve reached the end.
                  </div>
                )}


              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DiscoverManagement;
